import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** A list of tick boxes over a set of names. */
function Checks({ legend, hint, names, chosen, onToggle, label }) {
  return (
    <fieldset className="connection-field">
      <legend>{legend}</legend>
      {hint && <p className="source-hint">{hint}</p>}
      <div className="connection-checks">
        {names.length === 0 && <span className="source-hint">None found in Jira.</span>}
        {names.map((n) => (
          <label key={n.value}>
            <input type="checkbox" aria-label={`${label} ${n.label}`} checked={chosen.includes(n.value)} onChange={() => onToggle(n.value)} />
            {n.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Which projects to read, what counts as a defect and as a customer impacting
 * incident, and how much history and how many API calls to allow.
 */
export default function JiraSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const s = connection.definitions || {};
        setOptions(res.data);
        setDraft({
          interval: connection.syncIntervalMinutes,
          projects: s.projects || [],
          defectResolutions: s.defectResolutions || [],
          defectIssueTypes: s.defectIssueTypes || [],
          incidentIssueTypes: s.incidentIssueTypes || [],
          incidentPriorities: s.incidentPriorities || [],
          historyMonths: s.historyMonths,
          monthlyCallBudget: s.monthlyCallBudget,
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from Jira')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from Jira…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };
  const toggle = (name) => (value) => set({ [name]: draft[name].includes(value) ? draft[name].filter((v) => v !== value) : [...draft[name], value] });
  const asNames = (list) => list.map((n) => ({ value: n, label: n }));

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          projects: draft.projects,
          defectResolutions: draft.defectResolutions,
          defectIssueTypes: draft.defectIssueTypes,
          incidentIssueTypes: draft.incidentIssueTypes,
          incidentPriorities: draft.incidentPriorities,
          historyMonths: Number(draft.historyMonths),
          monthlyCallBudget: Number(draft.monthlyCallBudget),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      <Checks legend="Projects" label="Project" hint="Leave all unticked to read every project the account can see."
        names={options.projects.map((p) => ({ value: p.key, label: `${p.name} (${p.key})` }))}
        chosen={draft.projects} onToggle={toggle('projects')} />

      <Checks legend="Resolutions that mean a defect" label="Defect resolution"
        hint="Post-release defects count issues with these resolutions or the issue types below. With nothing ticked, Bugs and issues resolved as a bug, defect or software error count."
        names={asNames(options.resolutions)} chosen={draft.defectResolutions} onToggle={toggle('defectResolutions')} />
      <Checks legend="Issue types that mean a defect" label="Defect issue type"
        names={asNames(options.issueTypes)} chosen={draft.defectIssueTypes} onToggle={toggle('defectIssueTypes')} />

      <Checks legend="Issue types that mean a customer impacting incident" label="Incident issue type"
        hint="Customer impacting incidents count issues of these types or the priorities below. With nothing ticked, Incident issue types count."
        names={asNames(options.issueTypes)} chosen={draft.incidentIssueTypes} onToggle={toggle('incidentIssueTypes')} />
      <Checks legend="Priorities that mean a customer impacting incident" label="Incident priority"
        names={asNames(options.priorities)} chosen={draft.incidentPriorities} onToggle={toggle('incidentPriorities')} />

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>

      <label className="connection-field">
        <span>Monthly API call budget</span>
        <input type="number" min="100" max="1000000" step="100" value={draft.monthlyCallBudget} onChange={(e) => set({ monthlyCallBudget: e.target.value })} />
      </label>
      <p className="source-hint">Counts only calls made by the wallboard, not other integrations using Jira.</p>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}
